// ./src/hooks/useDrawerMount.ts
//
// A drawer that is MOUNTED only while it is used must still slide in and out (suggested task "Make create-in-place
// drawers slide in", 2026-10-03; TaxSelect's #78 fix, made reusable).
//
// ⚠⚠ Mantine animates a Drawer only when `opened` goes false → true AFTER it mounted, and animates closing only while it
//   stays mounted. So `{x && <SomeDrawer opened … />}` — and a `React.lazy` drawer, which suspends and then mounts
//   already open — pops in with no slide, vanishes on close, and never hands focus back to what opened it.
//
//   useDrawerMount(target)  — the drawer is mounted closed, opened on the NEXT commit, and kept mounted through the exit
//                             transition; `value` keeps the last target (the guest, the entity…) so it can slide out
//                             with its content after the caller has cleared it.
//   useLazyDrawer(load)     — the code is loaded on the first open (a plain `import()`, NOT React.lazy), then the same:
//                             mounted closed, opened on the next commit, and kept mounted afterwards.
// ⓘ Exit: AppDrawer's transition is 200ms; DRAWER_EXIT_MS leaves a little over that before unmounting.
import { useCallback, useEffect, useRef, useState } from "react";

/** How long a closing drawer stays mounted (AppDrawer's transition is 200ms). */
export const DRAWER_EXIT_MS = 260;

export interface DrawerMount<T> {
  /** Render the drawer only while this is true. */
  mounted: boolean;
  /** Pass as the drawer's `opened`. */
  opened: boolean;
  /** The last open target — still set while the drawer slides out. */
  value: T | null;
}

/**
 * Mount a drawer for as long as `target` is set (plus its exit transition), opening it one commit after it mounted so
 * it slides in. `target` may be a boolean or the thing being edited.
 */
export function useDrawerMount<T>(target: T | null | undefined | false, exitMs: number = DRAWER_EXIT_MS): DrawerMount<T> {
  const open = !!target;
  const lastRef = useRef<T | null>(open ? (target as T) : null);

  if (open) {
    lastRef.current = target as T;
  }

  const [mounted, setMounted] = useState(open);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (open) {
      if (!mounted) {
        // Mount it CLOSED first; this effect runs again once it has, and opens it then.
        setMounted(true);

        return undefined;
      }

      setOpened(true);

      return undefined;
    }

    setOpened(false);

    if (!mounted) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setMounted(false);
      lastRef.current = null;
    }, exitMs);

    return () => window.clearTimeout(timer);
  }, [open, mounted, exitMs]);

  return { mounted, opened: open && opened, value: lastRef.current };
}

export interface LazyDrawer<C> {
  /** The loaded component (null until the first open). Render it whenever it is set — it stays mounted. */
  Component: C | null;
  /** Pass as the drawer's `opened`. */
  opened: boolean;
  /** Asked to be open (true from the click, before the code has arrived) — for a caller pausing its own shortcuts. */
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

/**
 * A drawer whose code is loaded on its first open. ⚠ `load` must return the COMPONENT (e.g.
 * `() => import("…/X").then((m) => m.X)`) — never a `React.lazy` wrapper, which is exactly what popped in.
 */
export function useLazyDrawer<C>(load: () => Promise<C>, options: { onLoadError?: (error: unknown) => void } = {}): LazyDrawer<C> {
  const [Component, setComponent] = useState<C | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const loadRef = useRef(load);
  loadRef.current = load;
  const onLoadErrorRef = useRef(options.onLoadError);
  onLoadErrorRef.current = options.onLoadError;
  const loadingRef = useRef(false);

  // Opened one commit AFTER the component is mounted closed (on the first open that is the commit it arrived in).
  useEffect(() => {
    setOpened(!!Component && isOpen);
  }, [Component, isOpen]);

  const open = useCallback(() => {
    setIsOpen(true);

    if (Component || loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    void loadRef
      .current()
      .then((component) => setComponent(() => component))
      // ⚠ Not silent: a chunk that will not load (a stale one after a deploy, no network) would otherwise make the
      //   "Create …" action do nothing at all. The caller says so.
      .catch((error) => {
        setIsOpen(false);
        onLoadErrorRef.current?.(error);
      })
      .finally(() => {
        loadingRef.current = false;
      });
  }, [Component]);

  const close = useCallback(() => setIsOpen(false), []);

  return { Component, opened: opened && isOpen, isOpen, open, close };
}
